import RequestApi from '@/marketingBasic/api/RequestApi'
import {
  ApplicationServiceSetSettingsPayload,
  MarketbaseAutomaticReferenceCheckMode,
  Requestapplicationv2Settings
} from '@/types/api'
import {
  REFERENCE_CHECK_QUERY_MODES,
  type ReferenceCheckQueryMode,
  type ReferenceCheckSettings
} from '@/types/models'

export interface ApplicationSettingsPatch extends Partial<ReferenceCheckSettings> {
  showContractCreationCostAgreement?: boolean
  contractCreationCost?: number
}

export const REFERENCE_LINK_PLACEHOLDER = '#link_referenz#'
export const APPLICANT_NAME_PLACEHOLDER = '#name_interessent#'

const DEFAULT_MAX_AFFORDABILITY_PERCENT = 33
const DEFAULT_TOLERANCE_PERCENT = 35

const MEDIA_SELECTOR = 'img, video, iframe, object, embed, svg, canvas'

function hasMedia(element: Element): boolean {
  return Boolean(element.querySelector(MEDIA_SELECTOR))
}

function countBrTags(html: string): number {
  const matches = html.match(/<br\s*\/?>/gi)
  return matches ? matches.length : 0
}

function isWhitespaceOnlyText(node: Node): boolean {
  return node.nodeType === Node.TEXT_NODE && !(node.textContent || '').replace(/\u00a0/g, ' ').trim()
}

function isEmptyEmailBlock(element: Element): boolean {
  if (hasMedia(element)) {
    return false
  }
  const textContent = (element.textContent || '').replace(/\u00a0/g, ' ').trim()
  return textContent === ''
}

function isDivOrParagraph(node: Node): node is Element {
  return node.nodeType === Node.ELEMENT_NODE &&
    ((node as Element).tagName === 'DIV' || (node as Element).tagName === 'P')
}

const PHRASING_TAGS = new Set([
  'A', 'ABBR', 'B', 'BR', 'CITE', 'CODE', 'EM', 'FONT', 'I', 'KBD',
  'MARK', 'Q', 'S', 'SAMP', 'SMALL', 'SPAN', 'STRIKE', 'STRONG',
  'SUB', 'SUP', 'TIME', 'U', 'VAR', 'WBR'
])

function isPhrasingOrText(node: Node): boolean {
  if (node.nodeType === Node.TEXT_NODE || node.nodeType === Node.COMMENT_NODE) {
    return true
  }
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return false
  }
  return PHRASING_TAGS.has((node as Element).tagName)
}

function isFlatInlineMarkup(container: Element): boolean {
  return Array.from(container.childNodes).every(isPhrasingOrText)
}

function createBlankBlock(doc: Document): HTMLDivElement {
  const div = doc.createElement('div')
  div.innerHTML = '&nbsp;'
  return div
}

/**
 * TinyMCE often keeps a trailing caret <br> in non-empty blocks; strip one trailing br
 * so it does not become a spurious blank line.
 */
function childNodesWithoutTrailingCaretBr(block: Element): Node[] {
  const nodes = Array.from(block.childNodes)
  while (nodes.length > 0 && isWhitespaceOnlyText(nodes[nodes.length - 1])) {
    nodes.pop()
  }
  if (nodes.length > 0 && nodes[nodes.length - 1].nodeName === 'BR') {
    nodes.pop()
  }
  return nodes
}

/**
 * Split a block's children on <br> runs so each visual blank line becomes its own padded div.
 * e.g. Hallo<br><br><br>test -> <div>Hallo</div><div>&nbsp;</div>x3<div>test</div>
 */
function expandBlockByBreaks(block: Element, doc: Document): Node[] {
  const result: Node[] = []
  let current = doc.createElement('div')

  const flushCurrent = () => {
    if (current.childNodes.length === 0) {
      return
    }
    const text = (current.textContent || '').replace(/\u00a0/g, ' ').trim()
    const hasNonEmpty = text !== '' || Boolean(current.querySelector(MEDIA_SELECTOR))
    if (hasNonEmpty) {
      result.push(current)
    }
    current = doc.createElement('div')
  }

  const childNodes = childNodesWithoutTrailingCaretBr(block)
  let pendingBlankLines = 0

  const flushPendingBlanks = () => {
    for (let i = 0; i < pendingBlankLines; i++) {
      result.push(createBlankBlock(doc))
    }
    pendingBlankLines = 0
  }

  for (const child of childNodes) {
    if (child.nodeName === 'BR') {
      flushCurrent()
      pendingBlankLines += 1
      continue
    }
    if (isWhitespaceOnlyText(child) && current.childNodes.length === 0) {
      continue
    }
    if (pendingBlankLines > 0) {
      flushPendingBlanks()
    }
    current.appendChild(child.cloneNode(true))
  }

  flushCurrent()
  flushPendingBlanks()

  if (result.length === 0) {
    result.push(createBlankBlock(doc))
  }

  return result
}

function transformEmailBlock(block: Element, doc: Document): Node[] {
  if (hasMedia(block)) {
    return [block.cloneNode(true)]
  }

  const brCount = countBrTags(block.innerHTML)
  if (isEmptyEmailBlock(block)) {
    // Empty block: TinyMCE uses a single caret <br>; treat as 1 blank.
    // Multiple <br> mean that many blank lines.
    const blankCount = Math.max(brCount, 1)
    const blanks: Node[] = []
    for (let i = 0; i < blankCount; i++) {
      blanks.push(createBlankBlock(doc))
    }
    return blanks
  }

  if (brCount > 0) {
    return expandBlockByBreaks(block, doc)
  }

  return [block.cloneNode(true)]
}

function replaceContainerChildren(container: Element, nodes: Node[]): string {
  container.innerHTML = ''
  nodes.forEach(node => container.appendChild(node))
  return container.innerHTML
}

/**
 * Ensures empty / multi-break blocks become one padded &nbsp; div per visual blank line
 * so consecutive blank lines survive email HTML pipelines.
 */
export function preserveEmptyEmailBlocks(html: string): string {
  if (!html || typeof html !== 'string') {
    return html || ''
  }
  if (typeof document === 'undefined') {
    return html
  }
  const container = document.createElement('div')
  container.innerHTML = html

  const childNodes = Array.from(container.childNodes)
  const hasDivOrP = childNodes.some(isDivOrParagraph)

  if (!hasDivOrP) {
    // Tables, lists, and other non-inline markup must stay intact. Only expand
    // when the body is flat text / phrasing with <br> (or truly empty).
    if (!isFlatInlineMarkup(container)) {
      return container.innerHTML
    }
    const brCount = countBrTags(container.innerHTML)
    if (isEmptyEmailBlock(container) && brCount > 0) {
      const blanks: Node[] = []
      for (let i = 0; i < brCount; i++) {
        blanks.push(createBlankBlock(document))
      }
      return replaceContainerChildren(container, blanks)
    }
    if (brCount > 0 || isEmptyEmailBlock(container)) {
      return replaceContainerChildren(container, expandBlockByBreaks(container, document))
    }
    return container.innerHTML
  }

  const replacement: Node[] = []
  childNodes.forEach(node => {
    if (!isDivOrParagraph(node)) {
      replacement.push(node.cloneNode(true))
      return
    }
    transformEmailBlock(node, document).forEach(next => replacement.push(next))
  })

  return replaceContainerChildren(container, replacement)
}

export function getDefaultReferenceCheckSettings(): ReferenceCheckSettings {
  return {
    queryMode: REFERENCE_CHECK_QUERY_MODES.NONE,
    senderMailboxId: null,
    requireDebtCollectionExtract: false,
    requireIdDocument: false,
    maxAffordabilityPercent: DEFAULT_MAX_AFFORDABILITY_PERCENT,
    tolerancePercent: DEFAULT_TOLERANCE_PERCENT,
    employerEmailTemplate: '',
    landlordEmailTemplate: ''
  }
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>
  }
  return null
}

function pick(record: Record<string, unknown> | null, ...keys: string[]): unknown {
  if (!record) {
    return undefined
  }
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(record, key) && record[key] !== undefined && record[key] !== null) {
      return record[key]
    }
  }
  return undefined
}

function toOptionalInt(value: unknown, fallback: number): number {
  if (value === null || value === undefined || value === '') {
    return fallback
  }
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return fallback
  }
  return Math.trunc(parsed)
}

export function toApiQueryMode(queryMode: ReferenceCheckQueryMode): MarketbaseAutomaticReferenceCheckMode {
  switch (queryMode) {
    case REFERENCE_CHECK_QUERY_MODES.AFFORDABILITY:
      return MarketbaseAutomaticReferenceCheckMode.AUTOMATIC_REFERENCE_CHECK_MODE_IF_AFFORDABILITY_APPROPRIATE
    case REFERENCE_CHECK_QUERY_MODES.COMPLETE_DOSSIER_AND_AFFORDABILITY:
      return MarketbaseAutomaticReferenceCheckMode.AUTOMATIC_REFERENCE_CHECK_MODE_IF_COMPLETE_DOSSIER_AND_AFFORDABILITY
    default:
      return MarketbaseAutomaticReferenceCheckMode.AUTOMATIC_REFERENCE_CHECK_MODE_DO_NOT_CHECK
  }
}

export function toObjectApiAutomaticReferenceCheck(
  queryMode: ReferenceCheckQueryMode,
  inheritsFromAccount: boolean
): MarketbaseAutomaticReferenceCheckMode {
  if (inheritsFromAccount) {
    return MarketbaseAutomaticReferenceCheckMode.AUTOMATIC_REFERENCE_CHECK_MODE_UNDEFINED
  }
  return toApiQueryMode(queryMode)
}

export function isUndefinedAutomaticReferenceCheck(mode?: unknown): boolean {
  if (mode === null || mode === undefined || mode === '' || mode === 0) {
    return true
  }
  const value = String(mode)
  return value === '0' ||
    value === MarketbaseAutomaticReferenceCheckMode.AUTOMATIC_REFERENCE_CHECK_MODE_UNDEFINED ||
    value.endsWith('AUTOMATIC_REFERENCE_CHECK_MODE_UNDEFINED')
}

export function isUndefinedReferenceSenderMailboxId(value?: unknown): boolean {
  return value === null || value === undefined || value === ''
}

export function toObjectApiReferenceSenderMailboxId(
  mailboxId: string | null | undefined,
  inheritsFromAccount: boolean
): string {
  if (inheritsFromAccount) {
    return ''
  }
  return mailboxId || ''
}

export function fromApiQueryMode(mode?: unknown): ReferenceCheckQueryMode {
  const value = String(mode || '')
  if (
    mode === 2 ||
    value === MarketbaseAutomaticReferenceCheckMode.AUTOMATIC_REFERENCE_CHECK_MODE_IF_AFFORDABILITY_APPROPRIATE ||
    value.endsWith('IF_AFFORDABILITY_APPROPRIATE') ||
    value === REFERENCE_CHECK_QUERY_MODES.AFFORDABILITY
  ) {
    return REFERENCE_CHECK_QUERY_MODES.AFFORDABILITY
  }
  if (
    mode === 3 ||
    value === MarketbaseAutomaticReferenceCheckMode.AUTOMATIC_REFERENCE_CHECK_MODE_IF_COMPLETE_DOSSIER_AND_AFFORDABILITY ||
    value.endsWith('IF_COMPLETE_DOSSIER_AND_AFFORDABILITY') ||
    value === REFERENCE_CHECK_QUERY_MODES.COMPLETE_DOSSIER_AND_AFFORDABILITY
  ) {
    return REFERENCE_CHECK_QUERY_MODES.COMPLETE_DOSSIER_AND_AFFORDABILITY
  }
  return REFERENCE_CHECK_QUERY_MODES.NONE
}

function extractApiSettings(response: unknown): Record<string, unknown> | null {
  const data = asRecord(response)
  if (!data) {
    return null
  }
  const nested = asRecord(data.settings)
  if (nested) {
    return nested
  }
  if (
    'automaticReferenceCheck' in data ||
    'automatic_reference_check' in data ||
    'affordability' in data ||
    'applicationDossier' in data ||
    'application_dossier' in data ||
    'showContractCreationCostAgreement' in data ||
    'show_contract_creation_cost_agreement' in data ||
    'employerReferenceEmailTemplate' in data ||
    'employer_reference_email_template' in data ||
    'landlordReferenceEmailTemplate' in data ||
    'landlord_reference_email_template' in data
  ) {
    return data
  }
  return null
}

function toCurrentApiSettings(response: unknown): Requestapplicationv2Settings | null {
  const raw = extractApiSettings(response)
  if (!raw) {
    return null
  }
  const dossier = asRecord(pick(raw, 'applicationDossier', 'application_dossier'))
  const affordability = asRecord(pick(raw, 'affordability'))
  return {
    accountId: String(pick(raw, 'accountId', 'account_id') || ''),
    showContractCreationCostAgreement: Boolean(
      pick(raw, 'showContractCreationCostAgreement', 'show_contract_creation_cost_agreement')
    ),
    contractCreationCost: Number(pick(raw, 'contractCreationCost', 'contract_creation_cost') ?? -1),
    automaticReferenceCheck: (pick(raw, 'automaticReferenceCheck', 'automatic_reference_check') ||
      MarketbaseAutomaticReferenceCheckMode.AUTOMATIC_REFERENCE_CHECK_MODE_DO_NOT_CHECK) as MarketbaseAutomaticReferenceCheckMode,
    referenceSenderMailboxId: String(pick(raw, 'referenceSenderMailboxId', 'reference_sender_mailbox_id') || ''),
    applicationDossier: {
      requireDebtCollectionRegistryExtract: Boolean(
        pick(dossier, 'requireDebtCollectionRegistryExtract', 'require_debt_collection_registry_extract')
      ),
      requireIdDocument: Boolean(pick(dossier, 'requireIdDocument', 'require_id_document'))
    },
    affordability: {
      maximumAffordabilityPercent: toOptionalInt(
        pick(affordability, 'maximumAffordabilityPercent', 'maximum_affordability_percent'),
        DEFAULT_MAX_AFFORDABILITY_PERCENT
      ),
      toleranceRangeUpToPercent: toOptionalInt(
        pick(affordability, 'toleranceRangeUpToPercent', 'tolerance_range_up_to_percent'),
        DEFAULT_TOLERANCE_PERCENT
      )
    },
    employerReferenceEmailTemplate: String(
      pick(raw, 'employerReferenceEmailTemplate', 'employer_reference_email_template') || ''
    ),
    landlordReferenceEmailTemplate: String(
      pick(raw, 'landlordReferenceEmailTemplate', 'landlord_reference_email_template') || ''
    )
  }
}

export function fromApiSettings(response: unknown, language = 'de'): ReferenceCheckSettings {
  const defaults = getDefaultReferenceCheckSettings()
  const settings = toCurrentApiSettings(response)
  if (!settings) {
    return defaults
  }

  return {
    queryMode: fromApiQueryMode(settings.automaticReferenceCheck),
    senderMailboxId: settings.referenceSenderMailboxId || null,
    requireDebtCollectionExtract: Boolean(settings.applicationDossier?.requireDebtCollectionRegistryExtract),
    requireIdDocument: Boolean(settings.applicationDossier?.requireIdDocument),
    maxAffordabilityPercent: toOptionalInt(
      settings.affordability?.maximumAffordabilityPercent,
      defaults.maxAffordabilityPercent
    ),
    tolerancePercent: toOptionalInt(
      settings.affordability?.toleranceRangeUpToPercent,
      defaults.tolerancePercent
    ),
    employerEmailTemplate: settings.employerReferenceEmailTemplate || '',
    landlordEmailTemplate: settings.landlordReferenceEmailTemplate || ''
  }
}

function resolveEmailTemplate(
  patch: ApplicationSettingsPatch,
  currentApi: Requestapplicationv2Settings | null,
  side: 'employer' | 'landlord'
): string {
  const patchKey = side === 'employer' ? 'employerEmailTemplate' : 'landlordEmailTemplate'
  const currentKey = side === 'employer'
    ? 'employerReferenceEmailTemplate'
    : 'landlordReferenceEmailTemplate'

  if (patch[patchKey] !== undefined) {
    return patch[patchKey] as string
  }
  return currentApi?.[currentKey] || ''
}

export function buildFullSettingsPayload(
  currentApi: Requestapplicationv2Settings | null,
  patch: ApplicationSettingsPatch,
  language = 'de'
): ApplicationServiceSetSettingsPayload {
  const defaults = getDefaultReferenceCheckSettings()
  const current = currentApi ? fromApiSettings({ settings: currentApi }, language) : defaults
  const merged: ReferenceCheckSettings = {
    ...defaults,
    ...current,
    ...patch
  }

  return {
    automaticReferenceCheck: toApiQueryMode(merged.queryMode),
    referenceSenderMailboxId: merged.senderMailboxId || '',
    applicationDossier: {
      requireDebtCollectionRegistryExtract: merged.requireDebtCollectionExtract,
      requireIdDocument: merged.requireIdDocument
    },
    affordability: {
      maximumAffordabilityPercent: merged.maxAffordabilityPercent,
      toleranceRangeUpToPercent: merged.tolerancePercent
    },
    employerReferenceEmailTemplate: resolveEmailTemplate(patch, currentApi, 'employer'),
    landlordReferenceEmailTemplate: resolveEmailTemplate(patch, currentApi, 'landlord'),
    showContractCreationCostAgreement: patch.showContractCreationCostAgreement ??
      Boolean(currentApi?.showContractCreationCostAgreement),
    contractCreationCost: patch.contractCreationCost ??
      (currentApi?.contractCreationCost ?? -1)
  }
}

export function toApiPatch(
  patch: ApplicationSettingsPatch,
  language: string,
  currentApi?: Requestapplicationv2Settings | null
): ApplicationServiceSetSettingsPayload {
  const payload: ApplicationServiceSetSettingsPayload = {}

  if (patch.queryMode !== undefined) {
    payload.automaticReferenceCheck = toApiQueryMode(patch.queryMode)
  }
  if (patch.senderMailboxId !== undefined) {
    payload.referenceSenderMailboxId = patch.senderMailboxId || ''
  }
  if (patch.requireDebtCollectionExtract !== undefined || patch.requireIdDocument !== undefined) {
    payload.applicationDossier = {
      requireDebtCollectionRegistryExtract: patch.requireDebtCollectionExtract ??
        Boolean(currentApi?.applicationDossier?.requireDebtCollectionRegistryExtract),
      requireIdDocument: patch.requireIdDocument ??
        Boolean(currentApi?.applicationDossier?.requireIdDocument)
    }
  }
  if (patch.maxAffordabilityPercent !== undefined || patch.tolerancePercent !== undefined) {
    payload.affordability = {
      maximumAffordabilityPercent: toOptionalInt(
        patch.maxAffordabilityPercent,
        currentApi?.affordability?.maximumAffordabilityPercent ?? DEFAULT_MAX_AFFORDABILITY_PERCENT
      ),
      toleranceRangeUpToPercent: toOptionalInt(
        patch.tolerancePercent,
        currentApi?.affordability?.toleranceRangeUpToPercent ?? DEFAULT_TOLERANCE_PERCENT
      )
    }
  }
  if (patch.employerEmailTemplate !== undefined) {
    payload.employerReferenceEmailTemplate = patch.employerEmailTemplate
  }
  if (patch.landlordEmailTemplate !== undefined) {
    payload.landlordReferenceEmailTemplate = patch.landlordEmailTemplate
  }

  return payload
}

export function templateContainsReferenceLink(html: string): boolean {
  return Boolean(html && html.includes(REFERENCE_LINK_PLACEHOLDER))
}

function isNotFound(error: { response?: { status?: number } }): boolean {
  return Boolean(error.response && error.response.status === 404)
}

export function loadReferenceCheckSettings(accountId: string, language = 'de'): Promise<ReferenceCheckSettings> {
  return RequestApi.getApplicationFormSettings(accountId)
    .then((response: unknown) => fromApiSettings(response, language))
    .catch((error: { response?: { status?: number } }) => {
      if (!isNotFound(error)) {
        throw error
      }
      return getDefaultReferenceCheckSettings()
    })
}

function loadApiSettings(accountId: string): Promise<Requestapplicationv2Settings | null> {
  return RequestApi.getApplicationFormSettings(accountId)
    .then((response: unknown) => toCurrentApiSettings(response))
    .catch((error: { response?: { status?: number } }) => {
      if (!isNotFound(error)) {
        throw error
      }
      return null
    })
}

export function saveReferenceCheckSettings(
  accountId: string,
  language: string,
  patch: ApplicationSettingsPatch
): Promise<unknown> {
  return loadApiSettings(accountId).then(currentApi => {
    return RequestApi.setApplicationSettings(
      accountId,
      buildFullSettingsPayload(currentApi, patch, language)
    )
  })
}
